<!-- Shared step badge (error icon / done circle / custom icon / number-badge).
     Reused by the desktop track and the mobile panel rows so the state logic
     is authored once. -->
<ng-template #stepBadge let-step="step" let-i="i">
  @if (step.state === 'error') {
    <!-- Error: bare danger icon, no circle / ring / fill. -->
    <soft-ds-icon
      class="soft-progress-steps-status-icon"
      name="fa-solid fa-triangle-exclamation"
      color="danger"
    />
  } @else if (isDone(step, i) && visibleIndex() !== i) {
    <!-- Done: check glyph inside a component-styled green circle. The current
         step keeps its primary number badge even when done, so "you are here"
         never disappears behind a check. -->
    <span class="soft-progress-steps-done-circle">
      <soft-ds-icon name="fa-solid fa-check" size="xs" color="current" />
    </span>
  } @else if (step.icon) {
    <!-- Custom icon: bare ds-icon, no badge / circle / ring — same treatment
         as the status icons above (cf. the error icon). -->
    <soft-ds-icon
      class="soft-progress-steps-status-icon"
      [name]="step.icon"
      color="current"
    />
  } @else {
    <!-- Numbered steps reuse the number-badge primitive (outline / primary). -->
    <soft-ds-number-badge [variant]="numberBadgeVariant(i)">
      {{ i + 1 }}
    </soft-ds-number-badge>
  }
</ng-template>

<div class="soft-progress-steps-track">
  @for (
    step of visibleSteps();
    let i = $index;
    let first = $first;
    let last = $last;
    track i
  ) {
    <div
      class="soft-progress-steps-step"
      [class.is-current]="visibleIndex() === i"
      [class.is-done]="
        isDone(step, i) &&
        visibleIndex() !== i &&
        step.state !== 'error' &&
        step.state !== 'disabled'
      "
      [class.is-error]="step.state === 'error'"
      [class.is-inactive]="
        visibleIndex() !== i &&
        !isDone(step, i) &&
        step.state !== 'disabled' &&
        step.state !== 'error'
      "
      [class.is-disabled]="step.state === 'disabled'"
      [class.is-clickable]="isClickable(step, i)"
      [class.is-first]="first"
      [class.is-last]="last"
      [attr.role]="isClickable(step, i) ? (step.routerLink ? 'link' : 'button') : null"
      [attr.tabindex]="isClickable(step, i) ? 0 : null"
      (click)="handleClick($event, step, i)"
      (keydown.enter)="onStepKeydown($event, step, i)"
      (keydown.space)="step.routerLink ? null : onStepKeydown($event, step, i)"
      [routerLink]="isClickable(step, i) && step.routerLink ? step.routerLink : null"
      [queryParams]="$safeNavigationMigration(step.routerLinkParams?.queryParams)"
    >
      <ng-container *ngTemplateOutlet="stepBadge; context: { step: step, i: i }" />

      @if (showLabels() && hasLabel(step)) {
        <div class="soft-progress-steps-labels">
          @if (step.header) {
            <span class="soft-progress-steps-label ds-body-2">
              {{ step.header | translate }}
            </span>
          }
          @if (step.subHeader) {
            <span class="soft-progress-steps-sublabel ds-caption">
              {{ step.subHeader | translate }}
            </span>
          } @else if (reservesSublabel()) {
            <span class="soft-progress-steps-sublabel ds-caption" aria-hidden="true">
              &nbsp;
            </span>
          }
        </div>
      }
    </div>
  }
</div>

<!-- Mobile presentation: only horizontal hosts collapse below --ds-breakpoint-lg
     (the CSS media query display-switches this branch vs the track above);
     vertical is already a narrow-friendly stack and never renders it. -->
@if (orientation() === 'horizontal') {
  <div class="soft-progress-steps-mobile">
    <!-- Collapsed bar: whole surface is a pointer-only toggle target. The
         chevron button is the single accessible/keyboard control. -->
    <div
      class="soft-progress-steps-mobile-bar"
      (click)="toggleMobile()"
      data-testid="progress-steps-mobile-bar"
    >
      <span
        class="soft-progress-steps-mobile-count ds-body-2"
        data-testid="progress-steps-mobile-count"
      >
        {{
          {
            message: '#UI_CORE.COMPONENTS.PROGRESS_STEPS.STEP_OF_SHORT',
            params: {
              current: currentPosition(),
              total: visibleSteps().length,
            },
          } | translate
        }}
      </span>
      <div class="soft-progress-steps-mobile-segments">
        @for (step of visibleSteps(); track i; let i = $index) {
          <span
            class="soft-progress-steps-mobile-segment"
            [class.is-current]="visibleIndex() === i"
            [class.is-done]="visibleIndex() !== i && isDone(step, i)"
          ></span>
        }
      </div>

      <span class="soft-progress-steps-mobile-toggle-zone">
        <soft-ds-button
          #mobileTrigger
          variant="secondary"
          size="lg"
          mode="iconOnly"
          [aria-expanded]="mobileOpen()"
          [aria-controls]="panelId"
          [aria-label]="'#UI_CORE.COMPONENTS.PROGRESS_STEPS.TITLE' | translate"
          (click)="toggleMobile()"
          data-testid="progress-steps-mobile-toggle"
        >
          <ng-template softSlot="leading">
            <soft-ds-icon
              [name]="
                mobileOpen() ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'
              "
              aria-hidden="true"
            />
          </ng-template>
        </soft-ds-button>
      </span>
    </div>

    <!-- Expanded panel: viewport-fixed overlay positioned from the host's own
         rect (see the CSS comment for why it's `fixed` and not `absolute`),
         replacing the collapsed bar's slot without shifting the content below. -->
    @if (mobileOpen()) {
      <div
        class="soft-progress-steps-mobile-panel"
        [id]="panelId"
        [style.top.px]="panelRect()?.top"
        [style.left.px]="panelRect()?.left"
        [style.width.px]="panelRect()?.width"
        data-testid="progress-steps-mobile-panel"
      >
        <!-- No close button here — the bar's own toggle (chevron-up while
             open, always visible since scrolling closes the panel) is the
             single control for collapsing it; a second one would duplicate it. -->
        <div class="soft-progress-steps-mobile-panel-header">
          <div class="soft-progress-steps-mobile-title-group">
            <span class="soft-progress-steps-mobile-title ds-title-1-semibold">
              {{ '#UI_CORE.COMPONENTS.PROGRESS_STEPS.TITLE' | translate }}
            </span>
            <span class="soft-progress-steps-mobile-subtitle ds-body-2">
              {{
                {
                  message: '#UI_CORE.COMPONENTS.PROGRESS_STEPS.STEP_OF',
                  params: {
                    current: currentPosition(),
                    total: visibleSteps().length,
                  },
                } | translate
              }}
            </span>
          </div>
        </div>

        @for (step of visibleSteps(); let i = $index; let last = $last; track i) {
          <div
            class="soft-progress-steps-mobile-row"
            [class.is-clickable]="isClickable(step, i)"
            [class.is-disabled]="step.state === 'disabled'"
            [class.is-last]="last"
            [attr.role]="
              isClickable(step, i) ? (step.routerLink ? 'link' : 'button') : null
            "
            [attr.tabindex]="isClickable(step, i) ? 0 : null"
            (click)="handleMobileClick($event, step, i)"
            (keydown.enter)="onStepKeydown($event, step, i)"
            (keydown.space)="step.routerLink ? null : onStepKeydown($event, step, i)"
            [routerLink]="
              isClickable(step, i) && step.routerLink ? step.routerLink : null
            "
            [queryParams]="$safeNavigationMigration(step.routerLinkParams?.queryParams)"
            data-testid="progress-steps-mobile-step"
          >
            <ng-container *ngTemplateOutlet="stepBadge; context: { step: step, i: i }" />

            @if (showLabels() && hasLabel(step)) {
              <div class="soft-progress-steps-mobile-labels">
                @if (step.header) {
                  <span class="soft-progress-steps-mobile-label ds-body-2">
                    {{ step.header | translate }}
                  </span>
                }
                @if (step.subHeader) {
                  <span class="soft-progress-steps-mobile-sublabel ds-caption">
                    {{ step.subHeader | translate }}
                  </span>
                } @else if (reservesSublabel()) {
                  <span
                    class="soft-progress-steps-mobile-sublabel ds-caption"
                    aria-hidden="true"
                  >
                    &nbsp;
                  </span>
                }
              </div>
            }
          </div>
        }
      </div>
    }
  </div>
}

<!-- Content templates remain a single shared instance outside the CSS-swapped
     desktop/mobile presentations, so responsive changes never duplicate or hide them. -->
@if (hasAnyContentTemplate()) {
  @for (step of visibleSteps(); track i; let i = $index; let last = $last) {
    <div class="soft-progress-steps-content" [class.is-last]="last">
      @if (step.contentTemplate) {
        <ng-container *ngTemplateOutlet="step.contentTemplate" />
      }
    </div>
  }
}
